import React from "react"
import * as Yup from "yup"
import {
  Box, Text, Flex, VStack, Heading,
} from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import ColorSelect from "components/ReactHookForm/ColorSelect/ColorSelect"
import RadioButtons, { RadioOptionLabel } from "components/ReactHookForm/RadioButtons"
import Select from "components/ReactHookForm/Select"
import { useForms } from "queries/forms"
import {
  DioboxEvent, FormType, Website, WebsiteActionBehavior,
} from "sharedTypes"
import FormListItem from "pages/EventShow/WebPage/FormListItem"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"
import { DEFAULT_BUTTON_COLOR } from "components/elements/ButtonPreview"
import Spinner from "components/elements/Spinner"
import { RegistrationButtonFormValues } from "pages/EventShow/WebPage/types"

interface Props {
  eventId: string
  event: DioboxEvent
  website: Website
  onClose?: () => void
  onHide: () => void
  onChange?: (values: RegistrationButtonFormValues) => void
}

const ActionButton = ({
  eventId, event, website, onClose, onHide, onChange,
}: Props) => {
  const queryClient = useQueryClient()
  const { isLoading, data: forms } = useForms(eventId)

  const getActionBehavior = (action: string, formId?: string): string | null => {
    if (action === "") return null
    if (action === "form") return formId || null

    return "forms"
  }

  const onSubmit = (values: {
    action: string
    formId?: string
    actionText?: string
    rsvpButtonColor?: string
  }) => {
    const actionBehavior = getActionBehavior(values.action, values.formId)

    const updateData = {
      actionBehavior: actionBehavior as unknown as WebsiteActionBehavior | string | null,
      actionText: values.actionText || "",
      rsvpButtonColor: values.rsvpButtonColor || DEFAULT_BUTTON_COLOR,
    }

    return api.putWebsite(eventId, updateData as any)
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        onHide()
      })
  }

  if (isLoading) {
    return <Spinner />
  }

  if (!forms) {
    return null
  }

  const enabledNonRSVPForms = forms.data.filter(
    ({ disabled, type, private: isPrivate }) => !disabled && type !== FormType.RSVP && !isPrivate,
  )

  const getInitialAction = (actionBehavior: string | null): string => {
    if (!actionBehavior) return ""
    if (actionBehavior === "forms") return "forms"

    return "form"
  }

  const getInitialFormId = (
    actionBehavior: string | null,
    defaultFormId: string | null,
  ): string | null => {
    if (actionBehavior && actionBehavior !== "forms") return actionBehavior

    return defaultFormId
  }

  const actionBehaviorStr = website.actionBehavior as unknown as string | null
  const initialAction = getInitialAction(actionBehaviorStr)
  const initialFormId = getInitialFormId(
    actionBehaviorStr,
    enabledNonRSVPForms[0]?.externalId || null,
  )

  const validationSchema = Yup.object().shape({
    action: Yup.string(),
    formId: Yup.string().when("action", {
      is: "form",
      then: (schema) => schema.required("Please select a form"),
    }),
    actionText: Yup.string().when("action", {
      is: (val: string) => val !== "",
      then: (schema) => schema.required("Button text is required"),
    }),
    rsvpButtonColor: Yup.string().when("action", {
      is: (val: string) => val !== "",
      then: (schema) => schema.matches(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i, "Invalid color format"),
    }),
  })

  return (
    <ModalForm
      title="Registration Button"
      size="xl"
      onClose={onClose || onHide}
      initialValues={{
        action: initialAction,
        formId: initialFormId,
        actionText: website.actionText || "",
        rsvpButtonColor: website.rsvpButtonColor || DEFAULT_BUTTON_COLOR,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      disableSubmitWhenNotDirty
      onChange={(values) => {
        if (onChange) {
          const actionBehavior = getActionBehavior(values.action, values.formId)
          onChange({
            actionEnabled: true,
            actionBehavior: actionBehavior as WebsiteActionBehavior | null,
            actionText: values.actionText || "",
            rsvpButtonColor: values.rsvpButtonColor || DEFAULT_BUTTON_COLOR,
          })
        }
      }}
    >
      {({ control, watch }) => {
        const action = watch("action")
        const enableSingleFormSelect = action === "form"
        const showMultipleForms = action === "forms"

        const singleFormSelect = (
          <Flex w="100%" pl={7}>
            <Select
              control={control}
              name="formId"
              disabled={!enableSingleFormSelect}
              options={enabledNonRSVPForms.map(
                ({ externalId, name }) => (
                  { label: name, value: externalId }
                ),
              )}
              mb={2}
            />
          </Flex>
        )

        const buttonLinkOptions = [
          {
            label: (
              <RadioOptionLabel
                title="Hide Button"
                description={<Text fontSize="sm" color="gray.600">Hide the registration button from the event page.</Text>}
                spacing={0.5}
              />
            ),
            value: "",
          },
          {
            label: (
              <RadioOptionLabel
                title="Open a Single Form"
                spacing={0.5}
              />
            ),
            value: "form",
            additionalElement: singleFormSelect,
          },
          {
            label: (
              <RadioOptionLabel
                title="Show Multiple Forms"
                description={(
                  <Text fontSize="sm" color="gray.600">
                    Show all public forms on the event page, allowing users to complete
                    {" "}
                    more than one form in a single session. To hide a form, set its visibility
                    {" "}
                    to Private.
                  </Text>
                )}
                spacing={0.5}
              />
            ),
            value: "forms",
          },
        ]

        return (
          <VStack gap={8} align="stretch">
            <VStack align="flex-start" gap={2}>
              <Text fontSize="md" mb={4}>
                Select the registration form your event page button will open.
              </Text>
              <Heading fontSize="xl" fontWeight="normal" mb={6}>Button Link</Heading>
              <RadioButtons
                spacing={4}
                optionGap={2}
                name="action"
                control={control}
                options={buttonLinkOptions}
              />
              {showMultipleForms && (
                <VStack ml={7} align="flex-start" spacing={3}>
                  {enabledNonRSVPForms.map(
                    (form) => <FormListItem key={form.externalId} event={event} form={form} />,
                  )}
                </VStack>
              )}
            </VStack>
            <Box>
              <Heading fontSize="xl" fontWeight="normal" mb={4}>Button Style</Heading>
              <VStack spacing={4} align="stretch">
                <TextField
                  name="actionText"
                  control={control}
                  label="Button Text"
                  variant="outline"
                />
                <ColorSelect
                  name="rsvpButtonColor"
                  control={control}
                  label="Button Color"
                />
              </VStack>
            </Box>
          </VStack>
        )
      }}
    </ModalForm>
  )
}

export default ActionButton
